import React from 'react';

export type MemberFormValues = {
  loginId: string;
  password: string;
  passwordConfirm: string;
  memberNo: string;
  manager: string;
  store: string;
  post1: string;
  post2: string;
  pref: string;
  city: string;
  address1: string;
  address2: string;
  tel: string;
  email: string;
  managerEmail: string;
  contractNo: string;
  otherPayments: {
    card: boolean;
    paid: boolean;
  };
  rescueRegister: string;
  paymentTerm: string;
  remarks: string;
  memo: string;
};

type MemberFormProps = {
  form: MemberFormValues;
  setForm: React.Dispatch<React.SetStateAction<MemberFormValues>>;
  showPassword: boolean;
  setShowPassword: React.Dispatch<React.SetStateAction<boolean>>;
  showPasswordConfirm: boolean;
  setShowPasswordConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
};

const PREFS = [
  '東京都', '大阪府', '愛知県', '北海道', '福岡県', 'その他'
];
const PAYMENT_TERMS = [
  { value: 1, label: '末締めの翌月末銀行振込' },
  { value: 9, label: 'その他(備考欄に締日・支払日をご記入ください)' },
];

const MemberForm: React.FC<MemberFormProps> = ({
  form,
  setForm,
  showPassword,
  setShowPassword,
  showPasswordConfirm,
  setShowPasswordConfirm,
  handleChange,
}) => (
  <div className="space-y-5">
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">ログインID</label>
        <input type="text" className="w-full px-2 py-1 text-gray-500 bg-gray-100 border border-gray-200 rounded" value={form.loginId} disabled />
      </div>
      <div>
        <label className="block mb-1 text-sm font-semibold text-gray-500">会員番号</label>
        <input type="text" className="w-full px-2 py-1 text-gray-500 bg-gray-100 border border-gray-200 rounded" value={form.memberNo} disabled />
      </div>
    </div>
    {/* パスワード欄追加 */}
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">パスワード</label>
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          name="password"
          className="w-full px-2 py-1 pr-10 border border-gray-200 rounded"
          value={form.password}
          onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))}
          autoComplete="new-password"
        />
        <button
          type="button"
          className="absolute text-gray-400 -translate-y-1/2 right-2 top-1/2 hover:text-gray-600"
          tabIndex={-1}
          onClick={() => setShowPassword(v => !v)}
          aria-label={showPassword ? '非表示' : '表示'}
        >
          {showPassword ? (
            <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M2 10s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.5"/><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/></svg>
          ) : (
            <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M2 10s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.5"/><path d="M4 4l12 12" stroke="currentColor" strokeWidth="1.5"/></svg>
          )}
        </button>
      </div>
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">パスワード確認</label>
      <div className="relative">
        <input
          type={showPasswordConfirm ? 'text' : 'password'}
          name="passwordConfirm"
          className="w-full px-2 py-1 pr-10 border border-gray-200 rounded"
          value={form.passwordConfirm}
          onChange={e => setForm(prev => ({ ...prev, passwordConfirm: e.target.value }))}
          autoComplete="new-password"
        />
        <button
          type="button"
          className="absolute text-gray-400 -translate-y-1/2 right-2 top-1/2 hover:text-gray-600"
          tabIndex={-1}
          onClick={() => setShowPasswordConfirm(v => !v)}
          aria-label={showPasswordConfirm ? '非表示' : '表示'}
        >
          {showPasswordConfirm ? (
            <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M2 10s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.5"/><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/></svg>
          ) : (
            <svg width="20" height="20" fill="none" viewBox="0 0 20 20"><path d="M2 10s3-5 8-5 8 5 8 5-3 5-8 5-8-5-8-5z" stroke="currentColor" strokeWidth="1.5"/><path d="M4 4l12 12" stroke="currentColor" strokeWidth="1.5"/></svg>
          )}
        </button>
      </div>
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">担当者名</label>
      <input type="text" name="manager" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.manager} onChange={handleChange} />
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">店舗名</label>
      <input type="text" name="store" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.store} onChange={handleChange} />
    </div>
    <div className="flex gap-2">
      <div className="w-1/2">
        <label className="block mb-1 text-sm font-semibold text-gray-500">郵便番号（上3桁）</label>
        <input type="text" name="post1" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.post1} onChange={handleChange} maxLength={3} />
      </div>
      <div className="w-1/2">
        <label className="block mb-1 text-sm font-semibold text-gray-500">郵便番号（下4桁）</label>
        <input type="text" name="post2" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.post2} onChange={handleChange} maxLength={4} />
      </div>
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">都道府県</label>
      <select name="pref" className="w-full px-2 py-1 text-sm font-semibold text-gray-500 border border-gray-200 rounded" value={form.pref} onChange={handleChange}>
        <option value="">選択してください</option>
        {PREFS.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">市区町村</label>
      <input type="text" name="city" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.city} onChange={handleChange} />
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">住所１</label>
      <input type="text" name="address1" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.address1} onChange={handleChange} />
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">住所２</label>
      <input type="text" name="address2" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.address2} onChange={handleChange} />
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">電話番号</label>
      <input type="text" name="tel" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.tel} onChange={handleChange} />
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">メールアドレス</label>
      <input type="email" name="email" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.email} onChange={handleChange} />
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">管理者メールアドレス（任意）</label>
      <input type="email" name="managerEmail" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.managerEmail} onChange={handleChange} />
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">法人契約No</label>
      <input type="text" name="contractNo" className="w-full px-2 py-1 border border-gray-200 rounded" value={form.contractNo} onChange={handleChange} />
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">その他決済登録</label>
      <div className="flex flex-col gap-1 pl-1">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-500">
          <input
            type="checkbox"
            name="otherPayments.card"
            checked={form.otherPayments.card}
            onChange={handleChange}
          />カード決済
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-500">
          <input
            type="checkbox"
            name="otherPayments.paid"
            checked={form.otherPayments.paid}
            onChange={handleChange}
          />Paid
        </label>
      </div>
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">定額レスキュー登録</label>
      <div className="flex gap-6 pl-1 mt-1">
        <label className="flex items-center gap-1 text-sm font-semibold text-gray-500">
          <input type="radio" name="rescueRegister" value="yes" checked={form.rescueRegister === 'yes'} onChange={handleChange} />
          <span>登録する</span>
        </label>
        <label className="flex items-center gap-1 text-sm font-semibold text-gray-500">
          <input type="radio" name="rescueRegister" value="no" checked={form.rescueRegister === 'no'} onChange={handleChange} />
          <span>登録しない</span>
        </label>
      </div>
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">お支払い条件</label>
      <select name="paymentTerm" className="w-full px-2 py-1 text-sm font-semibold text-gray-500 border border-gray-200 rounded" value={form.paymentTerm} onChange={handleChange}>
        <option value="">選択してください</option>
        {PAYMENT_TERMS.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">備考</label>
      <textarea name="remarks" className="w-full px-2 py-1 border border-gray-200 rounded" rows={3} value={form.remarks} onChange={handleChange} />
    </div>
    <div>
      <label className="block mb-1 text-sm font-semibold text-gray-500">メモ</label>
      <textarea name="memo" className="w-full px-2 py-1 border border-gray-200 rounded" rows={3} value={form.memo} onChange={handleChange} />
    </div>
  </div>
);

export default MemberForm;
